// src/components/LoadingSpinner.jsx
import React from 'react';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ThemeContext } from '../ThemeProvider';
import dolphinAnimation from './dolphin-lottie.aggressive.json';
  
const LoadingSpinner = ({ 
  size = 'md', 
  text, 
  showLogo = true, 
  fullPage = false,
  variant = 'default', // 'default' | 'inline'
  showText = true,
  className
}) => {
  const { t } = useTranslation();
  const isDarkMode = useSelector(state => state.ui?.isDarkMode);
  
  // Safely use theme context - fallback to default values if not available
  const themeContext = React.useContext(ThemeContext);
  
  // Default theme values that match the app's primary colors
  const defaultTheme = {
    palette: {
      primary200: 'rgba(58, 147, 85, 0.2)',
      primary500: '#3A9355'
    }
  };
  
  const theme = themeContext?.palette ? themeContext : defaultTheme;
  
  // Support different sizes with proper mapping
  const sizeConfig = {
    xs: {
      spinner: 'h-4 w-4',
      icon: 12,
      lottie: 24,
      borderWidth: '2px'
    },
    sm: { 
      spinner: 'h-8 w-8',
      icon: 16,
      lottie: 48,
      borderWidth: '2px'
    },
    md: { 
      spinner: 'h-12 w-12',
      icon: 24,
      lottie: 64,
      borderWidth: '3px'
    },
    lg: { 
      spinner: 'h-16 w-16',
      icon: 32,
      lottie: 96,
      borderWidth: '4px'
    },
    xl: { 
      spinner: 'h-24 w-24',
      icon: 48,
      lottie: 128,
      borderWidth: '5px'
    }
  };
  
  const currentSize = sizeConfig[size] || sizeConfig.md;
  const loadingText = text || t("salesManagement.common.loading", "Yükleniyor...");
  
  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // Inline variant for buttons, compact areas
  if (variant === 'inline') {
    return (
      <span 
        role="status"
        aria-busy="true"
        aria-live="polite"
        className={`inline-flex items-center gap-2 ${className || ''}`}
      >
        {showLogo && !prefersReducedMotion ? (
          <Lottie
            animationData={dolphinAnimation}
            loop
            autoplay
            style={{ width: currentSize.lottie, height: currentSize.lottie }}
            aria-label={loadingText}
          />
        ) : (
          <span 
            className={`${currentSize.spinner} rounded-full animate-spin`}
            style={{
              borderWidth: currentSize.borderWidth,
              borderStyle: 'solid',
              borderColor: theme.palette.primary200,
              borderTopColor: theme.palette.primary500
            }}
          />
        )}
        {showText && (
          <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {loadingText}
          </span>
        )}
      </span>
    );
  }

  const Wrapper = ({ children }) => (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={`flex flex-col items-center justify-center ${fullPage ? 'fixed inset-0 z-50 bg-white/80 dark:bg-gray-900/80' : 'min-h-[200px] h-full w-full'} p-6 ${className || ''}`}
      style={{ minHeight: fullPage ? '100vh' : '200px' }}
    >
      {children}
    </div>
  );

  return (
    <Wrapper>
      <div className="relative">
        {showLogo && !prefersReducedMotion ? (
          <Lottie
            animationData={dolphinAnimation}
            loop
            autoplay
            style={{ width: currentSize.lottie, height: currentSize.lottie }}
            aria-label={loadingText}
          />
        ) : (
          <div 
            className={`${currentSize.spinner} rounded-full animate-spin`}
            style={{
              borderWidth: currentSize.borderWidth,
              borderStyle: 'solid',
              borderColor: theme.palette.primary200,
              borderTopColor: theme.palette.primary500
            }}
          ></div>
        )}
      </div>
      
      {showText && (
        <>
          <p className={`mt-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {loadingText}
          </p>
        {/*   <div className="mt-2 h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative">
            <div 
              className="h-full animate-shimmer absolute inset-0"
              style={{ backgroundColor: theme.palette.primary500 }}
            ></div>
          </div> */}
        </>
      )}
    </Wrapper>
  );
};

export default LoadingSpinner;
